import { Box, Chip, Paper, Stack, Typography } from "@mui/material";

// The kit's own. These chips say why a tag is or is not in force, which
// is the sentence this component exists for, and a `Chip` takes no focus.
import Tooltip from "./Tooltip.jsx";

import Disclose from "./Disclose.jsx";

/**
 * Why you are seeing what you are seeing.
 *
 * `manifest ∩ user` is two limits, and a caller who sees fewer rows than they
 * expected deserves to know which one did it. The reader app (LO-15) proved
 * this is worth showing; here it is a shell concern so no page can forget.
 *
 * A screen this cannot answer for says what does govern it (`gate`) rather
 * than rendering an empty intersection, which reads as "permitted nothing".
 *
 * Colour is never the only signal — every state carries a word too, because a
 * permission rendered as a green dot is a permission a colour-blind operator
 * cannot read.
 *
 * ## It is a bar now, not a panel
 *
 * This shipped as a three-column panel carrying a hundred-word paragraph, and
 * it was the first thing on every screen in every Kernel app. So the first
 * thing a person met was an essay about an intersection, and the chart they
 * came for started below the fold. The rule was being obeyed and the point of
 * the rule was being lost: a qualification nobody reads is not a
 * qualification.
 *
 * **The first sentence of `gate` is the summary and the rest is the detail.**
 * Not a second, shorter copy written for the bar — one string, split at the
 * first full stop, because two copies of a governance sentence is how the
 * short one ends up saying something the long one stopped saying. Each `gate`
 * in this repo is written so its first sentence stands alone; one that is not
 * renders whole rather than truncated, which is the safe direction.
 */

/**
 * First sentence, and the rest.
 *
 * Deliberately dumb — first `". "` and no attempt at abbreviations, because a
 * sentence splitter that is clever is a sentence splitter that is wrong in a
 * way nobody notices. A result that is implausibly short or long means the
 * guess missed, and then the whole string is the summary and there is no
 * detail to open. Worse layout, no lost meaning; that is the right way round.
 */
function split(text) {
  const at = String(text || "").indexOf(". ");
  if (at < 20 || at > 160) return [text, null];
  return [text.slice(0, at + 1), text.slice(at + 2)];
}

function TagChip({ tag, state, title }) {
  const colours = {
    permitted: "kernel.permitted",
    // In the manifest, outside the user's groups: the app could show it, this
    // person cannot see it.
    "not-yours": "kernel.stale",
    // In the user's groups, outside the manifest: they could see it, the app
    // never asked for it. This is the one people misread as a bug.
    "not-declared": "kernel.attention",
  };
  return (
    <Tooltip title={title}>
      <Chip
        size="small"
        label={tag}
        variant={state === "permitted" ? "filled" : "outlined"}
        sx={{
          bgcolor: state === "permitted" ? "transparent" : undefined,
          borderColor: colours[state],
          color: colours[state],
          borderWidth: 1,
          borderStyle: "solid",
        }}
      />
    </Tooltip>
  );
}

function Label({ children }) {
  return (
    <Typography
      variant="overline"
      color="text.secondary"
      sx={{ display: "block", whiteSpace: "nowrap" }}
    >
      {children}
    </Typography>
  );
}

/** App access (e.g. `app-builder`, which opens Builder) is a group and not a
 *  tag: it widens no read, so it has no place in a strip about what you can
 *  see. The kernel names every such group `app-*` (`schema.APP_ACCESS`, held
 *  to that by a test) and never reports one in `permitted_tags`. */
const isAppAccess = (group) => String(group).startsWith("app-");

export default function Permission({ groups: rawGroups, effective, gate }) {
  const groups = (rawGroups || []).filter((g) => !isAppAccess(g));
  const permitted = effective?.permitted || [];
  const declared = effective?.declared || null;

  const permittedSet = new Set(permitted);
  const declaredSet = declared ? new Set(declared) : null;

  // Everything either side of the intersection, so the strip can say which
  // limit removed what rather than only showing the result.
  const all = new Set([...groups, ...permitted, ...(declared || [])]);

  const stateOf = (tag) => {
    if (permittedSet.has(tag)) return "permitted";
    if (declaredSet && !declaredSet.has(tag)) return "not-declared";
    return "not-yours";
  };

  const explain = {
    permitted: "Permitted here — the app declares it and you hold it.",
    "not-declared": "You hold this, but this app never asked for it.",
    "not-yours": "This app may read it, but you do not hold it.",
  };

  const [headline, rest] = split(gate);

  return (
    <Paper
      variant="outlined"
      sx={{ px: 2, py: 1.25, bgcolor: "background.paper", minWidth: 0 }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={{ xs: 1.5, md: 3 }}
        sx={{ alignItems: { md: "center" }, minWidth: 0 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Label>Your groups</Label>
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap" }}>
            {groups.length ? (
              groups.map((g) => (
                <Chip key={g} size="small" variant="outlined" label={g} />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                none
              </Typography>
            )}
          </Stack>
        </Box>

        {/* A rule rather than whitespace. Two facts on one line need to read
            as two facts, and at this height a gap alone does not do it. */}
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            alignSelf: "stretch",
            borderLeft: 1,
            borderColor: "divider",
          }}
        />

        <Box sx={{ minWidth: 0, flex: 1 }}>
          {effective ? (
            <>
              <Label>What you can see here</Label>
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ flexWrap: "wrap", alignItems: "center" }}
              >
                {all.size ? (
                  [...all].sort().map((tag) => (
                    <TagChip
                      key={tag}
                      tag={tag}
                      state={stateOf(tag)}
                      title={explain[stateOf(tag)]}
                    />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    none
                  </Typography>
                )}
                <Disclose label="What this means">
                  <Typography variant="body2" color="text.secondary">
                    {permitted.length
                      ? // The counterintuitive part, said out loud. Tags
                        // restrict, so a record with none is visible to
                        // everyone — people assume the opposite and then
                        // wonder why untagged rows appear.
                        "A row is visible only if every tag on it is permitted. Untagged rows are visible to everyone — the widest case, not the narrowest."
                      : "You are permitted no tags here, so you see untagged records only — not nothing, which is the part that surprises people."}
                  </Typography>
                </Disclose>
              </Stack>
            </>
          ) : gate ? (
            // An em dash here read as "you are permitted nothing", on a
            // screen where the truth is that tags are not what governs it. A
            // strip whose whole job is saying why you see what you see must
            // not go blank on the screens it cannot answer for.
            <>
              <Label>What governs this screen</Label>
              {/* No control when the split found nothing to put behind one.
                  A "More" that opens an empty box is worse than a long
                  sentence. */}
              {rest ? (
                <Disclose summary={headline} label="More">
                  <Typography variant="body2" color="text.secondary">
                    {rest}
                  </Typography>
                </Disclose>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {headline}
                </Typography>
              )}
            </>
          ) : (
            <>
              <Label>What you can see here</Label>
              <Typography variant="body2" color="text.secondary">
                —
              </Typography>
            </>
          )}
        </Box>
      </Stack>
    </Paper>
  );
}
